import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { Controller, useForm } from 'react-hook-form';
import {
	Alert,
	AlertColor,
	AlertTitle,
	Backdrop,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Snackbar,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { checkShowErrorMessage } from '../../../common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import makeStyles from '@mui/styles/makeStyles';
import { getChargedAgainstOptions, postAuthorization } from 'features/transactions/services';
import { ChargedAgainstOptionTypeEnum, IChargedAgainstOption } from 'features/transactions/types';
import { TransactionAuthorizationContext } from 'features/authorization/types/authorizationGuide';
import {
	noRuleAdmitsInstallments,
	normalizeCardStamp,
	resolveEffectiveAuthorizationChannel,
} from 'features/authorization/helpers/authorizationGuide';
import { isAmexStamp, resolveCallCenterPhone } from 'features/authorization/constants/authorizationChannels';
import { MerchantRulesSource, useMerchantRules } from 'features/authorization/merchantRules/useMerchantRules';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { RolesEnum } from 'features/common/types';
import { AuthorizationChannelGuide } from './AuthorizationChannelGuide';
import { MerchantNumberLookup } from './MerchantNumberLookup';
import { AuthorizationCopyPanel } from './AuthorizationCopyPanel';
import {
	ExistingAuthorizationForWarning,
	getMastercardSameDayAuthorizationWarning,
} from 'features/authorization/helpers/mastercardSameDayAuthorizationWarning';

function PaperComponent(props: PaperProps) {
	return (
		<Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
			<Paper {...props} />
		</Draggable>
	);
}

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

export interface CreateAuthorizationModalProps {
	transactionId: number;
	transactionContext: TransactionAuthorizationContext;
	existingAuthorizations?: ExistingAuthorizationForWarning[];
	open: boolean;
	handleClose: () => void;
	reloadList: () => void;
}

interface ICreateAuthorizationForm {
	transactionId: number;
	chargedAgainstOptionCode: string;
	chargedAgainstOptionType: string;
	amountToAuthorize: string;
	authorizationCode: string;
}

/** El aviso discreto cuando el modal usa la tabla fija del Power en lugar de las reglas de la base. */
const FALLBACK_NOTICES: Partial<Record<MerchantRulesSource, string>> = {
	fixed: 'Reglas de respaldo: la Support API no respondió',
	slow: 'Reglas de respaldo: la Support API no respondió',
	empty: 'Reglas de respaldo: la base no tiene reglas vigentes',
	denied: 'Reglas de respaldo: tu usuario no puede leer las reglas de la base',
};

const CARD_STAMP_LABELS: Record<string, string> = {
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: 'American Express',
	oca: 'Oca',
	cabal: 'Cabal',
	diners: 'Diners',
	other: 'Otro',
};

export function CreateAuthorizationModal({
	transactionId,
	transactionContext,
	existingAuthorizations = [],
	open,
	handleClose,
	reloadList,
}: CreateAuthorizationModalProps): JSX.Element {
	const classes = useStyles();
	const { auth } = useSelector((state: RootState) => state);
	const isAdminOrOperator =
		auth.user?.role === RolesEnum.SUPERADMIN ||
		auth.user?.role === RolesEnum.ADMIN ||
		auth.user?.role === RolesEnum.OPERATOR;

	const postAuthorizationDefaultValues: ICreateAuthorizationForm = {
		transactionId,
		chargedAgainstOptionCode: '',
		chargedAgainstOptionType: '',
		amountToAuthorize: '',
		authorizationCode: '',
	};

	const [chargedAgainstOptions, setChargedAgainstOptions] = useState<IChargedAgainstOption[]>([]);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [formDataToConfirm, setFormDataToConfirm] = useState<ICreateAuthorizationForm | null>(null);
	const [externalAuthConfirmed, setExternalAuthConfirmed] = useState(false);
	// Con las reglas de la base, si ninguna admite las cuotas: supervisión tiene que haberlas autorizado.
	const [supervisionConfirmed, setSupervisionConfirmed] = useState(false);
	const [copySnackbar, setCopySnackbar] = useState<string | null>(null);
	const confirmButtonRef = useRef<HTMLButtonElement>(null);

	const {
		control,
		setValue,
		watch,
		handleSubmit,
		formState: { errors, isValid },
		reset,
	} = useForm({ mode: 'all', defaultValues: postAuthorizationDefaultValues });

	const [loading, setLoading] = useState(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);

	const watchedCarrierCode = watch('chargedAgainstOptionCode');
	const watchedOptionType = watch('chargedAgainstOptionType');
	const watchedAmount = watch('amountToAuthorize');

	const selectedOption = useMemo(
		() => chargedAgainstOptions.find((opt) => opt.code === watchedCarrierCode && opt.optionType === watchedOptionType),
		[chargedAgainstOptions, watchedCarrierCode, watchedOptionType],
	);

	// Las reglas por aerolínea de la base (Mesa de cruces → Reglas de cruce), pedidas al abrir; si la Support API no
	// las da en 5 s, la tabla fija del Power. Mientras llegan, ni canal ni código de comercio.
	const merchantRules = useMerchantRules(open);
	const rulesLoading = merchantRules.source === 'loading';
	const fallbackNotice = FALLBACK_NOTICES[merchantRules.source];

	const authorizationChannel = useMemo(() => {
		if (!watchedCarrierCode || rulesLoading) return null;
		return resolveEffectiveAuthorizationChannel(
			watchedCarrierCode,
			transactionContext.cardStamp,
			transactionContext.fee,
			merchantRules.rules,
		);
	}, [watchedCarrierCode, transactionContext.cardStamp, transactionContext.fee, rulesLoading, merchantRules.rules]);

	const cardStampLabel =
		CARD_STAMP_LABELS[normalizeCardStamp(transactionContext.cardStamp)] || transactionContext.cardStamp;

	const callCenterConfirmationLabel = useMemo(() => {
		if (authorizationChannel !== 'call_center') return null;
		const phone = resolveCallCenterPhone(transactionContext.cardStamp);
		return isAmexStamp(transactionContext.cardStamp) ? `call center Amex AX (${phone})` : `call center Visa (${phone})`;
	}, [authorizationChannel, transactionContext.cardStamp]);

	const mastercardSameDayWarning = useMemo(
		() =>
			getMastercardSameDayAuthorizationWarning(
				transactionContext.cardStamp,
				existingAuthorizations,
				watchedCarrierCode,
				selectedOption?.name,
			),
		[transactionContext.cardStamp, existingAuthorizations, watchedCarrierCode, selectedOption?.name],
	);

	const handleCopyFeedback = (message: string): void => {
		setCopySnackbar(message);
	};

	// Sin canal todavía: no hay aerolínea elegida o se están cargando sus reglas.
	const installmentsWithoutRule =
		Boolean(watchedCarrierCode) &&
		!rulesLoading &&
		noRuleAdmitsInstallments(
			watchedCarrierCode,
			transactionContext.cardStamp,
			transactionContext.fee,
			merchantRules.rules,
		);

	const canSubmitForm =
		isValid &&
		externalAuthConfirmed &&
		Boolean(authorizationChannel) &&
		authorizationChannel !== 'no_cards' &&
		(!installmentsWithoutRule || supervisionConfirmed);

	const onSubmit = (data: ICreateAuthorizationForm): void => {
		setFormDataToConfirm({
			...data,
			authorizationCode: data.authorizationCode.trim(),
		});
		setShowConfirmation(true);
	};

	const handleConfirmAuthorization = async (): Promise<void> => {
		if (!formDataToConfirm) return;

		try {
			setLoading(true);
			await postAuthorization({
				...formDataToConfirm,
				authorizationCode: formDataToConfirm.authorizationCode.trim(),
				amountToAuthorize: Number(formDataToConfirm.amountToAuthorize),
				chargedAgainstOptionType: formDataToConfirm.chargedAgainstOptionType as ChargedAgainstOptionTypeEnum,
			});
			setAlert({
				show: true,
				severity: 'success',
				message: 'Autorización creada satisfactoriamente',
			});
			reset();
			setShowConfirmation(false);
			setFormDataToConfirm(null);
			setExternalAuthConfirmed(false);
			reloadList();
			handleClose();
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error la autorización'),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleBackToForm = (): void => {
		setShowConfirmation(false);
	};

	const handleModalClose = (): void => {
		setShowConfirmation(false);
		setFormDataToConfirm(null);
		setExternalAuthConfirmed(false);
		reset();
		handleClose();
	};

	const applyPendingAmount = (): void => {
		setValue('amountToAuthorize', String(transactionContext.pendingAuthorization), {
			shouldValidate: true,
			shouldDirty: true,
		});
	};

	const loadChargedAgainstOptionsToSelect = async (): Promise<void> => {
		try {
			setLoading(true);
			setChargedAgainstOptions((await getChargedAgainstOptions()).data);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado de cobrados contra'),
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			loadChargedAgainstOptionsToSelect();
		}
	}, [open]);

	useEffect(() => {
		if (!open) {
			setExternalAuthConfirmed(false);
			setShowConfirmation(false);
			setFormDataToConfirm(null);
		}
	}, [open]);

	useEffect(() => {
		setExternalAuthConfirmed(false);
	}, [watchedCarrierCode]);

	// Otra aerolínea, otro sello u otras cuotas (o el modal que se cierra): la confirmación de supervisión no vale más.
	useEffect(() => {
		setSupervisionConfirmed(false);
	}, [watchedCarrierCode, transactionContext.cardStamp, transactionContext.fee, open]);

	useEffect(() => {
		if (showConfirmation && confirmButtonRef.current) {
			setTimeout(() => confirmButtonRef.current?.focus(), 100);
		}
	}, [showConfirmation]);

	const isProblematicChar = (char: string): boolean => ['6', '9', '0', 'O', '1', 'l', 'I', '8', 'B'].includes(char);

	const getSelectedAirlineName = (): string => {
		if (!formDataToConfirm) return '-';
		const option = chargedAgainstOptions.find(
			(opt) =>
				opt.code === formDataToConfirm.chargedAgainstOptionCode &&
				opt.optionType === formDataToConfirm.chargedAgainstOptionType,
		);
		return option?.name || '-';
	};

	return (
		<div>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Dialog
				open={open}
				PaperComponent={PaperComponent}
				aria-labelledby="draggable-dialog-title"
				maxWidth="lg"
				fullWidth
			>
				<Toolbar
					id="draggable-dialog-title"
					style={{ cursor: 'move' }}
					sx={{ display: 'flex', justifyContent: 'space-between' }}
				>
					<Typography variant="h5" color="primary">
						{showConfirmation ? 'Verificar Código de Autorización' : 'Nueva autorización'}
					</Typography>
					<IconButton edge="start" color="inherit" onClick={handleModalClose} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Toolbar>
				<DialogContent>
					{!showConfirmation ? (
						<form onSubmit={handleSubmit(onSubmit)}>
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
								<Chip label={`Sello: ${cardStampLabel}`} size="small" color="primary" variant="outlined" />
								<Chip label={`${transactionContext.fee} cuotas`} size="small" variant="outlined" />
								<Chip
									label={`Pendiente: ${formatTransactionAmount(
										transactionContext.pendingAuthorization,
										transactionContext.coinCode,
									)}`}
									size="small"
									color="warning"
									variant="outlined"
								/>
							</Box>

							{mastercardSameDayWarning && (
								<Alert
									severity={mastercardSameDayWarning.level === 'critical' ? 'error' : 'warning'}
									variant={mastercardSameDayWarning.level === 'critical' ? 'filled' : 'outlined'}
									sx={{
										mb: 2,
										...(mastercardSameDayWarning.level === 'critical' && {
											border: 2,
											borderColor: 'error.dark',
											boxShadow: 2,
										}),
									}}
								>
									<AlertTitle sx={{ fontWeight: 700, mb: mastercardSameDayWarning.level === 'critical' ? 0.5 : 0 }}>
										{mastercardSameDayWarning.title}
									</AlertTitle>
									<Typography variant="body2" fontWeight={mastercardSameDayWarning.level === 'critical' ? 600 : 400}>
										{mastercardSameDayWarning.message}
									</Typography>
								</Alert>
							)}

							<Box
								sx={{
									display: 'grid',
									gap: 3,
									gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
									gridTemplateRows: { xs: 'auto auto auto', md: 'auto auto' },
									gridTemplateAreas: {
										xs: '"step1" "step2" "step3"',
										md: '"step1 step2" "step3 step2"',
									},
								}}
							>
								<Box sx={{ gridArea: 'step1' }}>
									<Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
										1 · Configuración
									</Typography>
									<Controller
										name="chargedAgainstOptionCode"
										control={control}
										rules={{
											required: {
												value: true,
												message: 'Seleccioná el validating carrier',
											},
										}}
										render={({ field }) => {
											const selectedOptionValue: IChargedAgainstOption | '' =
												chargedAgainstOptions.find(
													(option) => option.code === field.value && option.optionType === watchedOptionType,
												) || '';

											return (
												<FormControl fullWidth sx={{ mb: 2 }}>
													<InputLabel id="charged-against-label">Cobrado contra</InputLabel>
													<Select
														labelId="charged-against-label"
														label="Cobrado contra"
														disabled={!chargedAgainstOptions.length || loading}
														autoFocus={!loading && chargedAgainstOptions.length > 0}
														value={selectedOptionValue || ''}
														onChange={(e: SelectChangeEvent<IChargedAgainstOption | ''>) => {
															const selected = e.target.value as IChargedAgainstOption | '';
															if (selected) {
																setValue('chargedAgainstOptionType', selected.optionType);
																field.onChange(selected.code);
															} else {
																setValue('chargedAgainstOptionType', '');
																field.onChange('');
															}
														}}
													>
														{chargedAgainstOptions.map((option) => (
															// @ts-expect-error MUI Select value object
															<MenuItem key={`${option.code}-${option.optionType}`} value={option}>
																{option.name}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											);
										}}
									/>

									<Controller
										name="amountToAuthorize"
										control={control}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
											min: { value: 1, message: 'El monto debe ser mayor que 0' },
											max: {
												value: transactionContext.pendingAuthorization,
												message: `No puede superar ${formatTransactionAmount(
													transactionContext.pendingAuthorization,
													transactionContext.coinCode,
												)}`,
											},
											pattern: {
												value: /^(0|[1-9]\d*)(\.\d+)?$/,
												message: 'El valor debe ser numérico',
											},
										}}
										render={({ field }) => (
											<TextField
												{...field}
												variant="outlined"
												required
												fullWidth
												label="Monto a autorizar"
												type="number"
												disabled={loading}
												error={Boolean(errors.amountToAuthorize)}
												helperText={checkShowErrorMessage(
													Boolean(errors.amountToAuthorize),
													errors.amountToAuthorize?.message,
												)}
												sx={{ mb: 1 }}
											/>
										)}
									/>
									<Button
										variant="outlined"
										size="small"
										fullWidth
										onClick={applyPendingAmount}
										disabled={transactionContext.pendingAuthorization <= 0}
									>
										Autorizar pendiente ·{' '}
										{formatTransactionAmount(transactionContext.pendingAuthorization, transactionContext.coinCode)}
									</Button>
								</Box>

								<Box sx={{ gridArea: 'step2', alignSelf: { md: 'start' } }}>
									<Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
										2 · Autorizar en el canal correcto
									</Typography>

									{fallbackNotice && (
										<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
											{fallbackNotice}
										</Typography>
									)}

									{!watchedCarrierCode && (
										<Alert severity="info">Seleccioná un validating carrier para ver la guía de autorización.</Alert>
									)}

									{watchedCarrierCode && rulesLoading && (
										<Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
											<CircularProgress size={18} />
											<Typography variant="body2" color="text.secondary">
												Cargando las reglas de la aerolínea…
											</Typography>
										</Box>
									)}

									{watchedCarrierCode && authorizationChannel && (
										<Box
											sx={{
												display: 'grid',
												gridTemplateAreas: '"guide" "merchant" "copy"',
												gridTemplateRows: 'auto auto auto',
												gap: 1.5,
											}}
										>
											{/* DOM primero = tab primero (cód. comercio); visualmente abajo */}
											<Box sx={{ gridArea: 'copy' }}>
												<AuthorizationCopyPanel
													transactionContext={transactionContext}
													carrierCode={watchedCarrierCode}
													amountToAuthorize={watchedAmount}
													channel={authorizationChannel}
													onCopied={handleCopyFeedback}
													isAdminOrOperator={isAdminOrOperator}
													rules={merchantRules.rules}
												/>
											</Box>

											<Box sx={{ gridArea: 'merchant' }}>
												<MerchantNumberLookup
													carrierCode={watchedCarrierCode}
													cardStamp={transactionContext.cardStamp}
													fee={transactionContext.fee}
													onCopied={handleCopyFeedback}
													rules={merchantRules.rules}
													supervisionConfirmed={supervisionConfirmed}
													onSupervisionConfirmedChange={setSupervisionConfirmed}
												/>
											</Box>

											<Box sx={{ gridArea: 'guide' }}>
												<AuthorizationChannelGuide
													channel={authorizationChannel}
													carrierName={selectedOption?.name || watchedCarrierCode}
													cardStamp={transactionContext.cardStamp}
													cardStampLabel={cardStampLabel}
													onCopied={handleCopyFeedback}
												/>
											</Box>
										</Box>
									)}
								</Box>

								<Box sx={{ gridArea: 'step3' }}>
									<Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
										3 · Registrar en Power
									</Typography>
									<Controller
										name="authorizationCode"
										control={control}
										rules={{
											validate: (value) => {
												const normalized = value.trim();
												if (!normalized) return 'Este campo es requerido';
												if (normalized.length > 100) return 'Máximo 100 caracteres';
												return true;
											},
										}}
										render={({ field: { onBlur, ...field } }) => (
											<TextField
												{...field}
												onBlur={(e) => {
													field.onChange(e.target.value.trim());
													onBlur();
												}}
												variant="outlined"
												required
												fullWidth
												label="Código de autorización"
												disabled={loading || !watchedCarrierCode}
												error={Boolean(errors.authorizationCode)}
												helperText={checkShowErrorMessage(
													Boolean(errors.authorizationCode),
													errors.authorizationCode?.message,
												)}
												sx={{ mb: 2 }}
											/>
										)}
									/>

									<FormControlLabel
										control={
											<Checkbox
												checked={externalAuthConfirmed}
												onChange={(e) => setExternalAuthConfirmed(e.target.checked)}
												disabled={!watchedCarrierCode || !authorizationChannel || authorizationChannel === 'no_cards'}
											/>
										}
										label={
											<Typography variant="body2">
												Completé la autorización en{' '}
												{authorizationChannel === 'call_center'
													? callCenterConfirmationLabel
													: authorizationChannel === 'gds'
													? 'GDS'
													: 'Fiserv'}
											</Typography>
										}
										sx={{ mb: 2, alignItems: 'flex-start' }}
									/>

									<Button
										variant="contained"
										disabled={loading || !canSubmitForm}
										color="success"
										fullWidth
										type="submit"
									>
										Continuar a verificación
									</Button>
								</Box>
							</Box>
						</form>
					) : (
						<Box sx={{ p: 3 }}>
							<Alert severity="warning" sx={{ mb: 3, bgcolor: 'transparent', border: 2, borderColor: '#FF9800' }}>
								<Typography variant="h6" fontWeight="bold" gutterBottom>
									Comparalo dígito por dígito
								</Typography>
								<Typography variant="body2">Verificá con el código de autorización recibido.</Typography>
							</Alert>

							<Box sx={{ my: 4, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
								<Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={2}>
									Código ingresado:
								</Typography>
								<Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
									{formDataToConfirm?.authorizationCode.split('').map((char, index) => (
										<Box
											key={index}
											sx={{
												width: 70,
												height: 90,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: 3,
												borderColor: isProblematicChar(char) ? 'warning.dark' : 'primary.main',
												borderRadius: 2,
												bgcolor: isProblematicChar(char) ? 'warning.light' : 'white',
												fontFamily: 'monospace',
												fontSize: '3.5rem',
												fontWeight: 'bold',
											}}
										>
											{char}
										</Box>
									))}
								</Box>

								{formDataToConfirm?.authorizationCode.split('').some(isProblematicChar) && (
									<Alert severity="info" sx={{ mb: 2 }}>
										<Typography variant="caption">
											Los dígitos con fondo amarillo pueden confundirse (6/9, 0/O, 1/l/I, 8/B).
										</Typography>
									</Alert>
								)}

								<Divider sx={{ my: 3 }} />

								<Grid container spacing={3}>
									<Grid item xs={12} md={6}>
										<Typography variant="caption" color="text.secondary">
											Validating carrier
										</Typography>
										<Typography variant="h6" fontWeight="bold" color="primary">
											{getSelectedAirlineName()}
										</Typography>
									</Grid>
									<Grid item xs={12} md={6}>
										<Box
											sx={{
												p: 2.5,
												bgcolor: '#F5F5F5',
												borderRadius: 2,
												border: 3,
												borderColor: '#424242',
												textAlign: 'center',
											}}
										>
											<Typography variant="caption" color="text.secondary">
												Monto autorizado
											</Typography>
											<Typography variant="h4" fontWeight="bold" fontFamily="monospace">
												{formatTransactionAmount(
													Number(formDataToConfirm?.amountToAuthorize || 0),
													transactionContext.coinCode,
												)}
											</Typography>
										</Box>
									</Grid>
								</Grid>
							</Box>

							<Alert severity="error" sx={{ mb: 3, bgcolor: '#FFEBEE', border: 2, borderColor: '#EF5350' }}>
								<Typography variant="body1" fontWeight="bold">
									Si el código es incorrecto, los cruces serán rechazados y pueden generarse ADMs.
								</Typography>
								<Typography variant="body2">Esta acción no se puede deshacer.</Typography>
							</Alert>

							<Box sx={{ display: 'flex', gap: 2 }}>
								<Button
									variant="outlined"
									size="large"
									fullWidth
									onClick={handleBackToForm}
									disabled={loading}
									startIcon={<ArrowBackIcon />}
								>
									Volver y corregir
								</Button>
								<Button
									ref={confirmButtonRef}
									variant="contained"
									size="large"
									color="success"
									fullWidth
									onClick={handleConfirmAuthorization}
									disabled={loading}
									startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
								>
									{loading ? 'Creando...' : 'El código es correcto'}
								</Button>
							</Box>
						</Box>
					)}
				</DialogContent>
			</Dialog>
			<Snackbar
				open={Boolean(copySnackbar)}
				autoHideDuration={2000}
				onClose={() => setCopySnackbar(null)}
				message={copySnackbar}
			/>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
